/**
 * NetworkRfqPage — RFQ 工作台（列表 + 创建 + 详情）
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, FileText, Clock, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  rfqApi,
  circleApi,
  type RfqSummary,
  type RfqDetail,
  type Circle,
  RFQ_STATUS_LABELS,
  RFQ_STATUS_COLORS,
} from '@/lib/network-api';
import { cn } from '@/lib/utils';

function fmtDate(iso: string, withTime = false) {
  const d = new Date(iso);
  return withTime
    ? d.toLocaleString('en-SG', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
    : d.toLocaleDateString('en-SG', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

// ─── RFQ Status Icon ──────────────────────────────────

function RfqStatusIcon({ status }: { status: string }) {
  if (['awarded', 'completed'].includes(status)) return <CheckCircle2 className="size-3.5 text-green-600" />;
  if (['cancelled', 'expired'].includes(status)) return <XCircle className="size-3.5 text-destructive" />;
  if (status === 'draft') return <FileText className="size-3.5 text-muted-foreground" />;
  return <Clock className="size-3.5 text-amber-500" />;
}

// ─── RFQ Row ──────────────────────────────────────────

function RfqRow({ rfq, onClick }: { rfq: RfqSummary; onClick: () => void }) {
  const { t } = useTranslation();
  const statusColor = RFQ_STATUS_COLORS[rfq.status] ?? '';
  const statusLabels: Record<string, string> = {
    draft: t('networkPage.rfq.statuses.draft'),
    published: t('networkPage.rfq.statuses.published'),
    quoting: t('networkPage.rfq.statuses.quoting'),
    evaluating: t('networkPage.rfq.statuses.evaluating'),
    awarded: t('networkPage.rfq.statuses.awarded'),
    completed: t('networkPage.rfq.statuses.completed'),
    cancelled: t('networkPage.rfq.statuses.cancelled'),
    expired: t('networkPage.rfq.statuses.expired'),
  };
  const typeLabels: Record<RfqSummary['rfq_type'], string> = {
    spot: t('networkPage.rfq.typeSpot'),
    contract: t('networkPage.rfq.typeContract'),
    recurring: t('networkPage.rfq.typeRecurring'),
  };
  const statusLabel = statusLabels[rfq.status] ?? RFQ_STATUS_LABELS[rfq.status] ?? rfq.status;

  return (
    <div
      className="group flex cursor-pointer items-center gap-4 rounded-lg border border-border bg-card px-4 py-3 hover:border-primary/30 hover:shadow-sm"
      onClick={onClick}
    >
      <RfqStatusIcon status={rfq.status} />
      <div className="flex flex-1 flex-col gap-0.5 overflow-hidden">
        <span className="truncate font-medium text-foreground">{rfq.title}</span>
        <span className="text-xs text-muted-foreground">
          {rfq.circle_name ? `${rfq.circle_name} · ` : ''}
          {typeLabels[rfq.rfq_type]}
          {rfq.deadline && ` · ${t('networkPage.rfq.fieldDeadline')} ${fmtDate(rfq.deadline, true)}`}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {rfq._role === 'creator' && (
          <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{t('networkPage.rfq.creator')}</Badge>
        )}
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', statusColor)}>
          {statusLabel}
        </span>
        <ChevronRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </div>
    </div>
  );
}

// ─── RFQ Detail Panel ─────────────────────────────────

function RfqDetailPanel({ rfqId, onClose }: { rfqId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<RfqDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [submittingQuote, setSubmittingQuote] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    rfqApi.get(rfqId)
      .then(setDetail)
      .catch(() => toast.error(t('networkPage.rfq.detailLoadFailed')))
      .finally(() => setLoading(false));
  }, [rfqId, t]);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async () => {
    if (!detail) return;
    setPublishing(true);
    try {
      const updated = await rfqApi.publish(detail.id);
      setDetail(prev => prev ? { ...prev, status: updated.status } : prev);
      toast.success(t('networkPage.rfq.publishSuccess'));
    } catch (e: unknown) {
      toast.error((e as Error).message ?? t('networkPage.rfq.publishFailed'));
    } finally {
      setPublishing(false);
    }
  };

  const handleSubmitQuote = async () => {
    if (!detail || !quoteAmount) { toast.error(t('networkPage.rfq.quoteAmountRequired')); return; }
    setSubmittingQuote(true);
    try {
      await rfqApi.submitQuote(detail.id, {
        total_price: parseFloat(quoteAmount),
        notes: quoteNotes || undefined,
      });
      toast.success(t('networkPage.rfq.quoteSubmitSuccess'));
      setQuoteAmount(''); setQuoteNotes('');
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message ?? t('networkPage.rfq.quoteSubmitFailed'));
    } finally {
      setSubmittingQuote(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (!detail) return null;

  const isCreator = detail._role === 'creator';
  const statusColor = RFQ_STATUS_COLORS[detail.status] ?? '';
  const statusLabels: Record<string, string> = {
    draft: t('networkPage.rfq.statuses.draft'),
    published: t('networkPage.rfq.statuses.published'),
    quoting: t('networkPage.rfq.statuses.quoting'),
    evaluating: t('networkPage.rfq.statuses.evaluating'),
    awarded: t('networkPage.rfq.statuses.awarded'),
    completed: t('networkPage.rfq.statuses.completed'),
    cancelled: t('networkPage.rfq.statuses.cancelled'),
    expired: t('networkPage.rfq.statuses.expired'),
  };
  const typeLabels: Record<RfqDetail['rfq_type'], string> = {
    spot: t('networkPage.rfq.typeSpot'),
    contract: t('networkPage.rfq.typeContract'),
    recurring: t('networkPage.rfq.typeRecurring'),
  };
  const modeLabels: Record<RfqDetail['rfq_mode'], string> = {
    sealed: t('networkPage.rfq.modeSealed'),
    open: t('networkPage.rfq.modeOpen'),
    mixed: t('networkPage.rfq.modeMixed'),
  };
  const statusLabel = statusLabels[detail.status] ?? RFQ_STATUS_LABELS[detail.status] ?? detail.status;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div className="flex flex-col gap-1.5">
          <h3 className="font-semibold leading-tight text-foreground">{detail.title}</h3>
          <div className="flex items-center gap-2">
            <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', statusColor)}>
              {statusLabel}
            </span>
            <span className="text-xs text-muted-foreground">
              {modeLabels[detail.rfq_mode]}
            </span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>{t('networkPage.common.close')}</Button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <Tabs defaultValue="info" className="h-full">
          <TabsList className="w-full rounded-none border-b border-border bg-transparent px-6">
            <TabsTrigger value="info" className="flex-1">{t('networkPage.rfq.tabInfo')}</TabsTrigger>
            <TabsTrigger value="quotes" className="flex-1">{t('networkPage.rfq.tabQuotes')} {detail.quotes?.length ? `(${detail.quotes.length})` : ''}</TabsTrigger>
            {!isCreator && (detail.status === 'published' || detail.status === 'quoting') ? (
              <TabsTrigger value="submit" className="flex-1">{t('networkPage.rfq.tabSubmit')}</TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="info" className="px-6 py-4">
            {detail.description && (
              <p className="mb-4 text-sm text-muted-foreground">{detail.description}</p>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">{t('networkPage.rfq.fieldType')}</span>
                <span>{typeLabels[detail.rfq_type]}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">{t('networkPage.rfq.fieldCurrency')}</span>
                <span>{detail.currency}</span>
              </div>
              {detail.deadline && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs text-muted-foreground">{t('networkPage.rfq.fieldDeadline')}</span>
                  <span>{fmtDate(detail.deadline, true)}</span>
                </div>
              )}
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">{t('networkPage.rfq.fieldCreatedAt')}</span>
                <span>{fmtDate(detail.created_at)}</span>
              </div>
            </div>

            {detail.cargo_summary && Object.keys(detail.cargo_summary).length > 0 && (
              <div className="mt-4">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('networkPage.rfq.cargoSummary')}</h4>
                <div className="rounded-md bg-muted/50 p-3 text-xs">
                  <pre className="whitespace-pre-wrap font-mono">{JSON.stringify(detail.cargo_summary, null, 2)}</pre>
                </div>
              </div>
            )}

            {isCreator && detail.status === 'draft' && (
              <div className="mt-6">
                <Button onClick={handlePublish} disabled={publishing} className="w-full">
                  {publishing ? t('networkPage.rfq.publishSubmitting') : t('networkPage.rfq.publishAction')}
                </Button>
              </div>
            )}

            {detail.invitees && detail.invitees.length > 0 && (
              <div className="mt-4">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('networkPage.rfq.inviteesTitle', { count: detail.invitees.length })}
                </h4>
                <div className="flex flex-col gap-1.5">
                  {detail.invitees.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between rounded border border-border p-2 text-sm">
                      <span>{inv.tenant_name ?? inv.external_name ?? t('networkPage.common.unknown')}</span>
                      <Badge variant="outline" className="text-[10px]">{inv.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="quotes" className="px-6 py-4">
            {!detail.quotes || detail.quotes.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('networkPage.rfq.emptyQuotes')}</p>
            ) : (
              <div className="flex flex-col gap-2">
                {detail.quotes.map(q => (
                  <div key={q.id} className={cn(
                    'rounded-lg border p-3',
                    q.is_awarded ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : 'border-border bg-card'
                  )}>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold">
                        {detail.currency} {q.total_price?.toLocaleString() ?? q.unit_price?.toLocaleString() ?? 'TBD'}
                      </span>
                      {q.is_awarded && (
                        <Badge className="bg-green-600 text-white">{t('networkPage.rfq.awarded')}</Badge>
                      )}
                    </div>
                    {q.notes && <p className="mt-1 text-xs text-muted-foreground">{q.notes}</p>}
                    {q.submitted_at && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('networkPage.rfq.submittedAt', { date: fmtDate(q.submitted_at, true) })}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="submit" className="px-6 py-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label>{t('networkPage.rfq.quoteAmountLabel', { currency: detail.currency })}</Label>
                <Input
                  type="number"
                  placeholder="850.00"
                  value={quoteAmount}
                  onChange={e => setQuoteAmount(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('networkPage.rfq.quoteNotesLabel')}</Label>
                <Textarea
                  placeholder={t('networkPage.rfq.quoteNotesPlaceholder')}
                  rows={3}
                  value={quoteNotes}
                  onChange={e => setQuoteNotes(e.target.value)}
                />
              </div>
              <Button onClick={handleSubmitQuote} disabled={submittingQuote}>
                {submittingQuote ? t('networkPage.rfq.submitSubmitting') : t('networkPage.rfq.submitAction')}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─── Create RFQ Dialog ────────────────────────────────

function CreateRfqDialog({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (r: RfqSummary) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('spot');
  const [mode, setMode] = useState('sealed');
  const [circleId, setCircleId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [circles, setCircles] = useState<Circle[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      circleApi.list().then(setCircles).catch(() => {});
    }
  }, [open]);

  const handleCreate = async () => {
    if (!title.trim()) { toast.error(t('networkPage.rfq.titleRequired')); return; }
    setSaving(true);
    try {
      const selectedCircleId = circleId === '__all__' ? undefined : (circleId || undefined);
      const rfq = await rfqApi.create({
        title: title.trim(),
        description: description.trim() || undefined,
        rfq_type: type,
        rfq_mode: mode,
        circle_id: selectedCircleId,
        deadline: deadline ? new Date(deadline).toISOString() : undefined,
      });
      toast.success(t('networkPage.rfq.draftCreated'));
      onCreated(rfq as unknown as RfqSummary);
      setTitle(''); setDescription(''); setCircleId(''); setDeadline('');
    } catch (e: unknown) {
      toast.error((e as Error).message ?? t('networkPage.rfq.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const typeLabels = {
    spot: t('networkPage.rfq.typeSpot'),
    contract: t('networkPage.rfq.typeContract'),
    recurring: t('networkPage.rfq.typeRecurring'),
  };
  const modeLabels = {
    sealed: t('networkPage.rfq.modeSealed'),
    open: t('networkPage.rfq.modeOpen'),
    mixed: t('networkPage.rfq.modeMixed'),
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t('networkPage.rfq.createTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label>{t('networkPage.rfq.formTitleLabel')}</Label>
            <Input placeholder={t('networkPage.rfq.formTitlePlaceholder')} value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>{t('networkPage.rfq.formTypeLabel')}</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="spot">{typeLabels.spot}</SelectItem>
                  <SelectItem value="contract">{typeLabels.contract}</SelectItem>
                  <SelectItem value="recurring">{typeLabels.recurring}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('networkPage.rfq.formModeLabel')}</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sealed">{modeLabels.sealed}</SelectItem>
                  <SelectItem value="open">{modeLabels.open}</SelectItem>
                  <SelectItem value="mixed">{modeLabels.mixed}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('networkPage.rfq.formCircleLabel')}</Label>
            <Select value={circleId} onValueChange={setCircleId}>
              <SelectTrigger><SelectValue placeholder={t('networkPage.rfq.formCirclePlaceholder')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('networkPage.rfq.formCircleAny')}</SelectItem>
                {circles.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('networkPage.rfq.formDeadlineLabel')}</Label>
            <Input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('networkPage.rfq.formDescriptionLabel')}</Label>
            <Textarea placeholder={t('networkPage.rfq.formDescriptionPlaceholder')} rows={3} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('networkPage.common.cancel')}</Button>
          <Button onClick={handleCreate} disabled={saving}>
            {saving ? t('networkPage.rfq.createSubmitting') : t('networkPage.rfq.createAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ─────────────────────────────────────────

export default function NetworkRfqPage() {
  const { t } = useTranslation();
  const [rfqs, setRfqs] = useState<RfqSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState<'mine' | 'received'>('mine');

  const load = useCallback(() => {
    setLoading(true);
    rfqApi.list()
      .then(setRfqs)
      .catch(() => toast.error(t('networkPage.rfq.listLoadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const mine = rfqs.filter(r => r._role === 'creator');
  const received = rfqs.filter(r => r._role === 'invitee');
  const display = tab === 'mine' ? mine : received;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Main */}
      <div className={cn('flex flex-1 flex-col overflow-hidden', selectedId ? 'border-r border-border' : '')}>
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">{t('networkPage.rfq.pageTitle')}</h1>
            <p className="text-sm text-muted-foreground">{t('networkPage.rfq.pageSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="size-4" /> {t('networkPage.rfq.startAction')}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            className={cn('px-6 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === 'mine' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setTab('mine')}
          >
            {t('networkPage.rfq.tabMine')} {mine.length > 0 && `(${mine.length})`}
          </button>
          <button
            className={cn('px-6 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === 'received' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setTab('received')}
          >
            {t('networkPage.rfq.tabReceived')} {received.length > 0 && `(${received.length})`}
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : display.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-20 text-center">
              <FileText className="size-12 text-muted-foreground/40" />
              <div>
                <p className="font-medium text-muted-foreground">
                  {tab === 'mine' ? t('networkPage.rfq.emptyMineTitle') : t('networkPage.rfq.emptyReceivedTitle')}
                </p>
                {tab === 'mine' && (
                  <p className="mt-1 text-sm text-muted-foreground">{t('networkPage.rfq.emptyMineDesc')}</p>
                )}
              </div>
              {tab === 'mine' && (
                <Button onClick={() => setShowCreate(true)}>
                  <Plus className="size-4" /> {t('networkPage.rfq.startAction')}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {display.map(rfq => (
                <RfqRow
                  key={rfq.id}
                  rfq={rfq}
                  onClick={() => setSelectedId(s => s === rfq.id ? null : rfq.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedId && (
        <div className="w-[440px] shrink-0 overflow-hidden">
          <RfqDetailPanel rfqId={selectedId} onClose={() => setSelectedId(null)} />
        </div>
      )}

      {/* Create dialog */}
      <CreateRfqDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={rfq => {
          setRfqs(prev => [rfq, ...prev]);
          setShowCreate(false);
          setSelectedId(rfq.id);
          setTab('mine');
        }}
      />
    </div>
  );
}
